import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "lucide-react";
import { CalendarService } from "@/services/calendar-service";
import { AssetService } from "@/services/asset-service";
import { AssetLogo } from "@/components/asset/asset-logo";
import { cn } from "@/lib/utils";
import type {
  Asset,
  DividendCalendar,
  EarningsCalendar,
  PerformanceData,
} from "@/schemas";

type UpcomingEvent = {
  date: Date;
  type: "dividend" | "earnings";
  label: string;
  assetId: string;
  assetSymbol: string;
  assetName: string;
  assetType: string;
  assetWebsite?: string | null;
};

interface UpcomingEventsCardProps {
  performanceData: PerformanceData | null;
  className?: string;
}

const MAX_EVENTS = 5;
const RANGE_DAYS = 180;

const formatDate = (date: Date) =>
  date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const hasMeaningfulTime = (date: Date) =>
  date.getUTCHours() !== 0 ||
  date.getUTCMinutes() !== 0 ||
  date.getUTCSeconds() !== 0 ||
  date.getUTCMilliseconds() !== 0;

const formatTime = (date: Date) =>
  date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const toISODate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDaysUntil = (date: Date) => {
  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = Math.round(
    (startOfDay(date).getTime() - startOfDay(new Date()).getTime()) / msPerDay,
  );
  if (diff <= 0) return "Today";
  if (diff === 1) return "In 1 day";
  return `In ${diff} days`;
};

export function UpcomingEventsCard({
  performanceData,
  className,
}: UpcomingEventsCardProps) {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [assetsById, setAssetsById] = useState<Record<string, Asset>>({});

  const assetsMap = useMemo(() => {
    const assets = performanceData?.assets_performance || {};
    return new Map(Object.entries(assets));
  }, [performanceData]);

  const assetIds = useMemo(() => {
    return Array.from(assetsMap.entries())
      .filter(
        ([, asset]) =>
          !asset.is_closed &&
          (asset.asset_type === "stock" || asset.asset_type === "etf"),
      )
      .map(([assetId]) => assetId);
  }, [assetsMap]);

  useEffect(() => {
    let cancelled = false;
    const fetchAssets = async () => {
      if (assetIds.length === 0) {
        setAssetsById({});
        return;
      }
      try {
        const assets = await AssetService.getAssetsByIDs(assetIds);
        if (cancelled) return;
        const map: Record<string, Asset> = {};
        assets.forEach((asset) => {
          map[asset.asset_id] = asset;
        });
        setAssetsById(map);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load assets for upcoming events", error);
        }
      }
    };

    fetchAssets();
    return () => {
      cancelled = true;
    };
  }, [assetIds]);

  useEffect(() => {
    const fetchEvents = async () => {
      if (assetIds.length === 0) {
        setEvents([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const today = startOfDay(new Date());
      const endDate = addDays(today, RANGE_DAYS);

      try {
        const [dividendResult, earningsResult] = await Promise.all([
          CalendarService.getDividends({
            assetIds,
            startDate: toISODate(today),
            endDate: toISODate(endDate),
          }),
          CalendarService.getEarnings({
            assetIds,
            startDate: toISODate(today),
            endDate: toISODate(endDate),
          }),
        ]);

        const dividends = dividendResult.dividends;
        const earnings = earningsResult.earnings;

        const collected: UpcomingEvent[] = [];

        const addEvent = (
          assetId: string,
          dateStr: string | undefined,
          type: UpcomingEvent["type"],
          label: string,
        ) => {
          if (!dateStr) return;
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return;
          const eventDay = startOfDay(date);
          if (eventDay < today || eventDay > endDate) return;
          const asset = assetsMap.get(assetId);
          if (!asset) return;
          const fullAsset = assetsById[assetId];
          const website =
            fullAsset && "website" in fullAsset ? fullAsset.website : undefined;

          collected.push({
            date,
            type,
            label,
            assetId,
            assetSymbol: asset.asset_symbol,
            assetName: asset.asset_name,
            assetType: fullAsset?.type ?? asset.asset_type,
            assetWebsite: website ?? asset.asset_website,
          });
        };

        dividends.forEach((calendar: DividendCalendar) => {
          addEvent(
            calendar.asset_id,
            calendar.ex_dividend_date,
            "dividend",
            "Ex-dividend",
          );
          addEvent(
            calendar.asset_id,
            calendar.dividend_date,
            "dividend",
            "Dividend",
          );
        });

        earnings.forEach((calendar: EarningsCalendar) => {
          addEvent(
            calendar.asset_id,
            calendar.next_earnings_date,
            "earnings",
            "Earnings",
          );
        });

        const sorted = collected.sort(
          (a, b) => a.date.getTime() - b.date.getTime(),
        );
        setEvents(sorted.slice(0, MAX_EVENTS));
      } catch (error) {
        console.error("Failed to fetch upcoming events", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [assetIds, assetsMap, assetsById]);

  return (
    <Card className={cn("w-full flex flex-col", className)}>
      <CardHeader className="pb-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Upcoming Events
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-6 w-full" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            No upcoming events
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((event) => (
              <div
                key={`${event.assetId}-${event.type}-${event.date.toISOString()}`}
                className="grid grid-cols-[auto,1fr,auto] items-center gap-3 text-xs"
              >
                <div className="flex flex-col">
                  <span className="font-medium">{formatDate(event.date)}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {getDaysUntil(event.date)}
                  </span>
                  {event.type === "earnings" && hasMeaningfulTime(event.date) && (
                    <span className="text-[10px] text-muted-foreground">
                      {formatTime(event.date)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <AssetLogo
                    asset={{
                      type: event.assetType,
                      symbol: event.assetSymbol,
                      website: event.assetWebsite ?? undefined,
                    }}
                    size="sm"
                    className="border border-border/40"
                    showFallbackIcon
                  />
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {event.assetName || event.assetSymbol}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate hidden sm:block">
                      {event.assetSymbol}
                    </div>
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1.5 py-0 h-4 justify-self-end ${
                    event.type === "dividend"
                      ? "border-emerald-500/40 text-emerald-600"
                      : "border-indigo-500/40 text-indigo-600"
                  }`}
                >
                  {event.label}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
